import { ICON_RASTER_PX, iconFileName } from '@/lib/icons';

// SVG GOES IN, PNG COMES OUT, AND THE SVG NEVER LEAVES THE BROWSER.
//
// There is no vector format anywhere in this product's delivery path and that is deliberate:
// `UIImage` cannot decode an SVG, `URLSession` cannot fetch a `data:` URL (STATUS's risk table has
// the scars), and `apps/api/src/routes/assets.ts` accepts png/jpeg/webp/gif and nothing else. So
// the authoring tool is where vector becomes raster — once, at pick time, into an ordinary managed
// asset. Four SDKs then load a PNG like any other PNG.
//
// The RGB channel is nearly irrelevant: SPEC §5.5–5.9 makes `tint` paint the ALPHA MASK, so a
// tinted glyph takes its colour from a theme token and the baked pixels only show through when an
// author deliberately removes the tint. Black is the useful fallback — visible on the light
// surfaces that are the default everywhere.
const RASTER_INK = '#000000';

/** Normalize a rendered Lucide `<svg>` into the exact markup we rasterize. CLONES — the live icon
 *  in the picker keeps its Tailwind classes and its `currentColor`.
 *
 *  Three things have to go, and each of them is a silent wrong answer rather than an error:
 *  - `class`, because the picker renders at `size-5` and a rasterizer that inherited it would bake
 *    a 20px glyph into a 144px box.
 *  - `currentColor`, because an `<img>` loading a detached SVG has no cascade to resolve it against.
 *  - a missing `xmlns`, because without it the browser refuses to load the markup as an image at
 *    all — and refuses SILENTLY, as an `error` event on a blank picture. */
export function iconSvgMarkup(svg: SVGSVGElement, px: number = ICON_RASTER_PX): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(px));
  clone.setAttribute('height', String(px));
  if (!clone.getAttribute('viewBox')) clone.setAttribute('viewBox', '0 0 24 24');
  clone.removeAttribute('class');

  for (const el of [clone, ...Array.from(clone.querySelectorAll('*'))]) {
    el.removeAttribute('class');
    for (const attr of Array.from(el.attributes)) {
      if (attr.value === 'currentColor') el.setAttribute(attr.name, RASTER_INK);
    }
  }
  return new XMLSerializer().serializeToString(clone);
}

/** Markup → PNG bytes, via an `<img>` and a canvas.
 *
 *  The data URL is what keeps the canvas UNTAINTED: a same-origin `data:` source never sets the
 *  origin-clean flag, so `toBlob` is allowed to read the pixels back. A blob URL would work too and
 *  would need revoking; this does not. */
export async function rasterizeIconSvg(markup: string, px: number = ICON_RASTER_PX): Promise<Blob> {
  const image = new Image(px, px);
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    // The failure mode this catches is a malformed or namespace-less SVG, which otherwise renders
    // as an empty PNG the author only discovers on a device.
    image.onerror = () => reject(new Error('The icon could not be rasterized.'));
  });

  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('The icon could not be rasterized.');
  // NOT filled: the transparency IS the payload. A white background would make the mask a solid
  // square and every tinted icon a tinted block.
  ctx.drawImage(image, 0, 0, px, px);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The icon could not be rasterized.'))),
      'image/png'
    );
  });
}

/** The whole pick-time pipeline: a rendered icon and its catalogue name become an upload-ready
 *  `File` whose NAME is the dedup key (`icons.ts`). */
export async function iconFile(
  svg: SVGSVGElement,
  name: string,
  px: number = ICON_RASTER_PX
): Promise<File> {
  const blob = await rasterizeIconSvg(iconSvgMarkup(svg, px), px);
  return new File([blob], iconFileName(name, px), { type: 'image/png' });
}
